import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  ListItemIcon,
} from '@mui/material';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { SignatureRequirementClient } from 'clients/signature-requirement-client';
import { ISignatureRequirement } from 'models/signature-requirement/types';
import { formatInsuranceTrigger } from '../table-utils';
import { DeleteConfirmationModalStyles } from './delete-confirmation-modal.styles';

interface IDeleteModalProps {
  open: boolean;
  ruleIdsToDelete: number[];
  allRules: ISignatureRequirement[];
  onCancel: () => void;
  onSuccess: () => void;
}

type IDeleteConfirmationModalProps = IDeleteModalProps &
  WithStyles<typeof DeleteConfirmationModalStyles>;

const DeleteConfirmationModalComponent: React.FC<IDeleteConfirmationModalProps> = props => {
  const { classes } = props;
  const [loading, setLoading] = React.useState<boolean>(false);
  const [selectedRuleIds, setSelectedRuleIds] = React.useState<number[]>(props.ruleIdsToDelete);

  React.useEffect(() => {
    if (props.open) {
      setSelectedRuleIds(props.ruleIdsToDelete);
    }
  }, [props.open, props.ruleIdsToDelete]);

  const rulesToShow = props.allRules.filter(rule => props.ruleIdsToDelete.includes(rule.id));

  const handleToggle = (ruleId: number) => {
    setSelectedRuleIds(prev =>
      prev.includes(ruleId) ? prev.filter(id => id !== ruleId) : [...prev, ruleId],
    );
  };

  const handleDelete = async () => {
    if (selectedRuleIds.length === 0) return;

    setLoading(true);
    try {
      await SignatureRequirementClient.delete(selectedRuleIds);
      props.onSuccess();
      props.onCancel();
    } catch (error) {
      // TODO: Add notifyError
    } finally {
      setLoading(false);
    }
  };

  const getRuleName = (rule: ISignatureRequirement): string => {
    if (rule.trigger_type === 'insurance') {
      return formatInsuranceTrigger(
        rule.insurance_bin ?? null,
        rule.insurance_pcn ?? null,
        rule.insurance_group ?? null,
      );
    }
    return rule.name;
  };

  return (
    <Dialog open={props.open} onClose={props.onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Delete Signature Requirement Rules</DialogTitle>
      <DialogContent className={classes.modalContent}>
        <Typography className={classes.warningText}>
          ⚠️ Warning: This action is not reversible
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Once deleted, these rules must be recreated manually.
        </Typography>

        <div className={classes.rulesList}>
          <List>
            {rulesToShow.map(rule => (
              <ListItem key={rule.id} dense button onClick={() => handleToggle(rule.id)}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedRuleIds.includes(rule.id)}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary={getRuleName(rule)}
                  secondary={`Created by: User ${rule.created_by}`}
                />
              </ListItem>
            ))}
          </List>
        </div>

        <div className={classes.buttonContainer}>
          <Button onClick={props.onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={loading || selectedRuleIds.length === 0}
          >
            {loading ? 'Deleting...' : `Delete ${selectedRuleIds.length} Rule(s)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DeleteConfirmationModal = withStyles(DeleteConfirmationModalStyles)(
  DeleteConfirmationModalComponent,
);
